import React, { useState } from 'react'
import type { ColorResult } from 'react-color'
import { CustomPicker } from 'react-color'
import { Alpha, Hue, Saturation } from 'react-color/lib/components/common'
import type { ColorInputWithoutInstance } from 'tinycolor2'
import tinycolor from 'tinycolor2'

import { Checkboard } from '../../partials/Checkboard'
import {
  automatedThemeDefaults,
  ComponentsThemeProvider,
  getInternalTheme,
} from '../../providers/ComponentsThemeProvider'
import { Button } from '../Button'
import { ColorPickerInput } from './ColorPickerInput'
import * as S from './styled'
import type { ColorPickerProps, InjectedCustomPickerProps } from './types'

const CustomCircle = () => <S.CustomCircle />
const CustomPointer = () => <S.CustomPointer />
const CustomPointerAlpha = () => <S.CustomPointer alpha />

const ColorPickerComponent = ({
  hex,
  hsl,
  rgb,
  hsv,
  position = 'bottom-start',
  format = 'RGB',
  showAlpha = false,
  hexVisible = false,
  onChange,
  onValueChange,
}: ColorPickerProps & InjectedCustomPickerProps) => {
  const [showHex, setShowHex] = useState(false)
  const [showOpacity, setShowOpacity] = useState(showAlpha)

  const handleChange = (data: Partial<ColorResult> | ColorInputWithoutInstance) => {
    if (onChange) {
      onChange(data as ColorResult)
    }

    if (onValueChange) {
      const color = tinycolor(data as ColorInputWithoutInstance)

      if (format === 'RGB') {
        onValueChange(color.toRgbString())
      }

      if (format === 'HEX') {
        onValueChange(color.toHexString())
      }
    }
  }

  const handleOpacityInputChange = (data: string) => {
    let opacityValue = parseInt(data) / 100

    if (opacityValue < 0) {
      opacityValue = 0
    } else if (opacityValue > 1) {
      opacityValue = 1
    }

    return handleChange({
      r: Number(rgb?.r),
      g: Number(rgb?.g),
      b: Number(rgb?.b),
      a: opacityValue,
    })
  }

  const renderOpacity = () => {
    if (showOpacity) {
      return (
        <S.Alpha>
          <Alpha
            // @ts-ignore
            rgb={rgb}
            hsl={hsl}
            direction="vertical"
            pointer={CustomPointerAlpha}
            onChange={handleChange}
          />
        </S.Alpha>
      )
    }

    return null
  }

  const renderHexButton = () => {
    if (!showHex && !hexVisible) {
      return (
        <S.ButtonContainer>
          <Button
            label="Show HEX"
            appearance="primary"
            size="small"
            fullWidth
            onClick={() => setShowHex(true)}
          />
        </S.ButtonContainer>
      )
    }

    return null
  }

  const renderOpacityButton = () => {
    if (!showOpacity) {
      return (
        <S.ButtonContainer>
          <Button
            label="Show opacity"
            appearance="primary"
            size="small"
            fullWidth
            onClick={() => setShowOpacity(true)}
          />
        </S.ButtonContainer>
      )
    }

    return null
  }

  const renderButtons = () => {
    if (!showHex || !showOpacity) {
      return (
        <S.Buttons>
          {renderHexButton()}
          {format === 'RGB' && renderOpacityButton()}
        </S.Buttons>
      )
    }

    return null
  }

  const renderHexInput = () => {
    if (showHex || hexVisible) {
      return (
        <S.Input>
          <ColorPickerInput label="Hex" value={hex} onChange={handleChange} />
        </S.Input>
      )
    }

    return null
  }

  const renderOpacityInput = () => {
    if (showOpacity) {
      return (
        <S.Input>
          <ColorPickerInput
            label="Opacity"
            value={Math.round(rgb?.a ? rgb.a * 100 : 0).toString()}
            onChange={handleOpacityInputChange}
          />
        </S.Input>
      )
    }

    return null
  }

  const renderInputs = () => {
    if (showHex || showOpacity || hexVisible) {
      return (
        <S.Inputs withMargin={(format === 'RGB' && !showOpacity) || (!showHex && !hexVisible)}>
          {renderHexInput()}
          {renderOpacityInput()}
        </S.Inputs>
      )
    }

    return null
  }

  return (
    <ComponentsThemeProvider
      automatedTheme={getInternalTheme(automatedThemeDefaults.light)}
      mode="solid"
    >
      <S.Container pickerPosition={position} data-testid="ColorPicker">
        <S.ColorsContainer>
          <S.Saturation>
            <Saturation
              // @ts-ignore
              hsv={hsv}
              hsl={hsl}
              pointer={CustomCircle}
              onChange={handleChange}
            />
          </S.Saturation>

          <div>
            <S.SelectedContainer>
              <Checkboard />
              <S.Selected
                style={{
                  background: `rgba(${rgb?.r},${rgb?.g},${rgb?.b},${rgb?.a})`,
                }}
              />
            </S.SelectedContainer>

            <S.Modifiers>
              <S.Hue>
                <Hue
                  // @ts-ignore
                  hsl={hsl}
                  pointer={CustomPointer}
                  direction="vertical"
                  onChange={handleChange}
                />
              </S.Hue>

              {renderOpacity()}
            </S.Modifiers>
          </div>
        </S.ColorsContainer>

        {renderButtons()}

        {renderInputs()}
      </S.Container>
    </ComponentsThemeProvider>
  )
}

export const ColorPicker = CustomPicker(ColorPickerComponent)
